import {useState, useEffect, useRef, useCallback} from "react";
import {useSelector} from "react-redux";
import {
    useSearchThreadMutation,
    useCreateThreadMutation,
    useCreateThreadBackgroundRunMutation,
} from "../../../../../redux/api/langgraph/langgraphApiSlice";
import {useStream, UseStreamOptions} from "@langchain/langgraph-sdk/react";
import type {Message as LGMessage} from "@langchain/langgraph-sdk/dist/types.messages";
import {selectCurrentErikToken, selectCurrentErikUrl} from "../../../../../redux/features/authSlice";
import {useGetNegotiationDetailQuery} from "../../../../../redux/api/negotiationsApiSlice";
import {skipToken} from "@reduxjs/toolkit/dist/query";

interface UseChatThreadArgs {
    negotiationUuid: string;
    underwriterUuid: string;
    streamOptions?: Omit<UseStreamOptions, "apiUrl" | "defaultHeaders" | "assistantId">;
}

export function useChatThread({
                                  negotiationUuid,
                                  underwriterUuid,
                                  streamOptions,
                              }: UseChatThreadArgs) {
    const token = useSelector(selectCurrentErikToken);
    const url = useSelector(selectCurrentErikUrl);

    const [searchThread, {isLoading: loadingOrch, isError: orchError, error: orchErrorObj}] = useSearchThreadMutation();
    const [createThread, {isLoading: loadingCreate, isError: createError, error: createErrorObj}] = useCreateThreadMutation();
    const [createBgRun, {isLoading: loadingBg, isError: bgError, error: bgErrorObj}] = useCreateThreadBackgroundRunMutation();
    const {data: negotiationData} = useGetNegotiationDetailQuery(negotiationUuid ?? skipToken)

    const isInitializing = loadingOrch || loadingCreate || loadingBg;
    const initError = orchError
        ? (orchErrorObj as any).message
        : createError
            ? (createErrorObj as any).message
            : bgError
                ? (bgErrorObj as any).message
                : null;

    const [threadId, setThreadId] = useState<string | null>(null);
    const isThreadAlreadyInit = useRef(false)

    const initThreadFlow = useCallback(async () => {
        if (!negotiationUuid || !underwriterUuid) return;
        try {
            if (negotiationData?.negotiation_analysis?.thread_id) {
                const searchRes = await searchThread({
                    metadata: {graph_id: "chatbot"},
                    values: {negotiation_uuid: negotiationUuid, underwriter_uuid: underwriterUuid},
                }).unwrap();

                let id: string;
                if (Array.isArray(searchRes) && searchRes.length > 0) {
                    id = searchRes[0].thread_id;
                } else {
                    const createRes = await createThread().unwrap();
                    id = createRes.thread_id;

                    await createBgRun({
                        thread_id: id,
                        config: {
                            assistant_id: "chatbot",
                            input: {
                                underwriter_uuid: underwriterUuid,
                                negotiation_uuid: negotiationUuid,
                            },
                            config: {
                                configurable: {
                                    chat_model: "gpt-4.1",
                                    moderation_model: "gpt-4.1",
                                    prompt_template: "chatbot",
                                    recursion_limit: 20,
                                },
                            },
                        },
                    })
                }
                setThreadId(id);
            } else {
                setThreadId(null)
            }
        } catch (err: any) {
            console.log(err)
        }
    }, [createBgRun, createThread, negotiationData?.negotiation_analysis?.thread_id, negotiationUuid, searchThread, underwriterUuid])

    useEffect(() => {
        if (!isThreadAlreadyInit.current) {
            void initThreadFlow();
        }
    }, [initThreadFlow]);

    const retry = useCallback(() => {
        isThreadAlreadyInit.current = false;
        void initThreadFlow();
    }, [initThreadFlow]);

    // stream solo se ho un threadId valido
    const thread = useStream<{ messages: LGMessage[] }>({
        apiUrl: url || "",
        assistantId: "chatbot",
        defaultHeaders: token ? {Authorization: `JWT ${token}`} : {},
        threadId: threadId ?? undefined,
        ...streamOptions,
    });

    return {threadId, isInitializing, initError, thread, retry};
}
